import { Box } from '@chakra-ui/react';
import { FEATURE_FLAGS } from '~/constants';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { mapApiSearchContentResultsFromApiToVm } from '~/searchApi';

import { useContentWithMetrics } from '../../hooks/useContentWithMetrics';
import { useAddContentStore } from '../../sidebar/useAddContentStore';
import {
  mergeContentWithLinks,
  mergeContentWithMetrics,
} from '../../utils/contentUtils';
import { ContentCard } from './ContentCard/ContentCard';
import { ContentPreviewWrapper } from './ContentPreviewWrapper';
import { SingleContent } from './SingleContent';
import { TalentContentProperties } from './TalentContent';
import { getContentLink, getIsStoryExpired } from './utils';

export const TalentContentReadonly = ({
  customContent: existingContent,
  showInShared,
}: TalentContentProperties) => {
  const slug = existingContent[0].creator.slug;

  const { isEnabled: isMediaKitsContentMetrics } = useFeature(
    FEATURE_FLAGS.foamMediaKitsContentMetrics,
  );

  const { setIsContentDetailModalOpen } = useAddContentStore();

  // Extract content IDs for fetching metrics
  const contentIds = existingContent.map((item) => item.id);

  const showInSharedFlag = !isMediaKitsContentMetrics ? true : showInShared;

  // Conditionally fetch content with metrics only when showInShared is true
  const { data: contentWithMetrics } = useContentWithMetrics(
    contentIds,
    showInSharedFlag,
    true,
    slug,
  );

  // Merge metrics when visible; otherwise merge only link fields so we can render anchors
  const contentToMap = (() => {
    if (contentWithMetrics && contentWithMetrics.length > 0) {
      return showInSharedFlag
        ? mergeContentWithMetrics(existingContent, contentWithMetrics)
        : mergeContentWithLinks(existingContent, contentWithMetrics);
    }
    return existingContent;
  })();

  const mappedContent = mapApiSearchContentResultsFromApiToVm(contentToMap);

  // Use contentToMap directly when showInShared is true (it has all the data we need)
  // Use mappedContent when showInShared is false (original behavior)
  const contentToRender = showInSharedFlag ? contentToMap : mappedContent;

  return (
    <>
      <div className="flex shrink-0 snap-x snap-mandatory gap-4 overflow-x-auto tab:flex-wrap">
        {contentToRender?.map((content) => {
          const contentLink = getContentLink(content);
          const isStoryExpired = getIsStoryExpired(content);

          return (
            <Box
              key={content.id}
              className="relative shrink-0 grow snap-start overflow-hidden rounded-8 tab:max-w-[900px] last:tab:max-w-[500px]"
            >
              <ContentPreviewWrapper
                isStoryExpired={isStoryExpired}
                contentLink={contentLink}
              >
                {isMediaKitsContentMetrics ? (
                  <ContentCard
                    content={content}
                    isShared
                    showMetrics={showInSharedFlag}
                    onClick={() =>
                      setIsContentDetailModalOpen(true, content.contentId)
                    }
                  />
                ) : (
                  <SingleContent content={content} />
                )}
              </ContentPreviewWrapper>
            </Box>
          );
        })}
        <div className="grow-[999]">
          {/* This div is necessary to prevent last image to expand full width */}
        </div>
      </div>
    </>
  );
};
